import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { Heading, Text } from 'tio-ui/components/typography';
import { Chip } from 'tio-ui/components/buttons';
import { eq } from 'tio-ui/utilities';
import { PhWarning, PhArrowRight } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';

/**
 * SmartAction type representing a contextual action for the user
 */
export interface SmartAction {
  id: string;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  category: 'money' | 'career' | 'deadline' | 'opportunity';
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  title: string;
  description: string;
  impact?: string;
  cta: string;
  route: string;
}

/**
 * SmartActionCard - Displays a contextual action card with priority-based styling
 *
 * Smart actions surface personalized recommendations based on the user's situation.
 * Priority levels affect visual treatment:
 * - urgent: Red border, "Action needed" badge
 * - high: Amber border
 * - medium: Blue border
 * - low: Gray border
 *
 * @example
 * ```gts
 * <SmartActionCard @item={{this.smartAction}} />
 * ```
 */
interface SmartActionCardSignature {
  Args: {
    /** The smart action data to display */
    item: SmartAction;
  };
  Element: HTMLAnchorElement;
}

const getPriorityStyles = (priority: SmartAction['priority']) => {
  const styles = {
    urgent: {
      border: 'border-l-4 border-l-red-500',
      badge: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
      icon: 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400',
    },
    high: {
      border: 'border-l-4 border-l-amber-500',
      badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
      icon: 'bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400',
    },
    medium: {
      border: 'border-l-4 border-l-blue-500',
      badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
      icon: 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400',
    },
    low: {
      border: 'border-l-4 border-l-gray-300 dark:border-l-gray-600',
      badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      icon: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    },
  };
  return styles[priority];
};

// Helper to get styles in template
const priorityBorder = (priority: SmartAction['priority']) => getPriorityStyles(priority).border;
const priorityIcon = (priority: SmartAction['priority']) => getPriorityStyles(priority).icon;
const priorityBadge = (priority: SmartAction['priority']) => getPriorityStyles(priority).badge;

const SmartActionCard: TOC<SmartActionCardSignature> = <template>
  {{! template-lint-disable no-bare-strings }}
  <LinkTo
    @route={{@item.route}}
    class="flex flex-col bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm ring-1 ring-gray-950/[0.04] dark:ring-gray-700 hover:shadow-md transition-all
      {{priorityBorder @item.priority}}"
    role="article"
    aria-label="{{@item.title}}: {{@item.description}}"
    ...attributes
  >
    <div class="flex items-start gap-3 mb-3">
      <div class="p-2.5 rounded-xl flex-shrink-0 {{priorityIcon @item.priority}}">
        {{#let @item.icon as |ActionIcon|}}
          <ActionIcon @size={{20}} />
        {{/let}}
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 mb-1 flex-wrap">
          {{#if (eq @item.priority "urgent")}}
            <Chip class="text-xs {{priorityBadge @item.priority}}">
              <PhWarning @size={{12}} class="mr-1" />
              Action needed
            </Chip>
          {{/if}}
          {{#if @item.impact}}
            <span
              class="text-xs text-emerald-600 dark:text-emerald-400 font-medium"
            >{{@item.impact}}</span>
          {{/if}}
        </div>
        <Heading @level={{3}} class="text-sm font-semibold text-gray-900 dark:text-white">
          {{@item.title}}
        </Heading>
      </div>
    </div>

    <Text @intent="muted" class="text-sm dark:text-gray-400 mb-4 line-clamp-2">
      {{@item.description}}
    </Text>

    <div
      class="mt-auto flex items-center gap-1 text-violet-600 dark:text-violet-400 font-medium text-sm"
      aria-hidden="true"
    >
      <span>{{@item.cta}}</span>
      <PhArrowRight @size={{16}} />
    </div>
  </LinkTo>
</template>;

export default SmartActionCard;
